import { lazy, Suspense, useCallback, useMemo } from 'react';
import { createBrowserRouter, Outlet, RouterProvider, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { ScrollToTop } from '@/components/scroll-to-top';
import { MyAdsRenderer } from '@/page/my-ads-renderer';
import { ErrorState } from '@/page/v1/my-ads/components/error-state';

const UpgradeCheckout = lazy(() =>
	import('@/page/v1/upgrade-checkout').then(m => ({ default: m.UpgradeCheckoutPage }))
);

/**
 * Fallback shown for any path that does not match a declared route or
 * whenever a route element throws (network 404, unexpected runtime error).
 * Falls back to the listing on retry instead of leaving the user stuck
 * on React Router's default white-screen "Unexpected Application Error".
 */
const RouteErrorFallback = () => {
	const navigate = useNavigate();
	const handleRetry = useCallback(() => navigate('/', { replace: true }), [navigate]);
	return <ErrorState onRetry={handleRetry} />;
};

RouteErrorFallback.displayName = 'RouteErrorFallback';

const AppLayout = () => (
	<>
		<ScrollToTop />
		<Layout>
			<Outlet />
		</Layout>
	</>
);

AppLayout.displayName = 'AppLayout';

const ABRouter = () => {
	const router = useMemo(
		() =>
			createBrowserRouter(
				[
					{
						element: <AppLayout />,
						children: [
							{
								path: '/',
								element: (
									<Suspense fallback={null}>
										<MyAdsRenderer />
									</Suspense>
								),
								errorElement: <RouteErrorFallback />
							},
							{
								path: 'pagamento',
								element: (
									<Suspense fallback={<div className="min-h-[70vh]" />}>
										<UpgradeCheckout />
									</Suspense>
								),
								errorElement: <RouteErrorFallback />
							},
							{
								path: '*',
								element: <RouteErrorFallback />
							}
						]
					}
				],
				{ basename: import.meta.env.BASE_URL.replace(/\/$/, '') || '/' }
			),
		[]
	);

	return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
};

ABRouter.displayName = 'ABRouter';

export { ABRouter };
